import axios from "axios";

const state = () => ({
    items: [],
    totalCount: 0,
    isBusy: false,
    playerData: null,
    playerBankAccounts: null,
    resendActivationEmailMessage: '',
    resendPasswordResetEmailMessage: '',
    bonuses: [],
    currentPage: 1,
    perPage: 10
})

// getters
const getters = {
    isBusy: state => state.isBusy,
    items: state => state.items,
    totalCount: state => state.totalCount,
    fields: state => state.fields,
    player: state => state.playerData,
    playerBankAccounts: state => state.playerBankAccounts,
    resendActivationEmailMessage: state => state.resendActivationEmailMessage,
    resendPasswordResetEmailMessage: state => state.resendPasswordResetEmailMessage
}

// mutations
const mutations = {
    setBusy(state, data) {
        state.isBusy = data;
    },
    setBonuses(state, data) {
        state.bonuses = data;
    },
    setItems(state, response) {
        state.isBusy = false;
        state.items = response.data.items;
        state.totalCount = response.data.totalCount;
    },
    setPlayerData(state, data) {
        state.playerData = data;
    },
    setPlayerBankAccounts(state, data) {
      state.playerBankAccounts = data;
    },
    setResendActivationEmailMessage(state, data) {
        state.resendActivationEmailMessage = data;
    },
    setResendPasswordResetEmailMessage(state, data) {
        state.resendPasswordResetEmailMessage = data;
    },
    resetState(state) {
        Object.assign(state, state())
    },
    setCurrentPage(state, data) {
        state.currentPage = data;
    },
    setPerPage(state, data) {
        state.perPage = data;
    }
}

// actions
const actions = {
    // eslint-disable-next-line no-unused-vars
    manualCredit({state, commit}, payload) {
        commit('setBusy', true);
        return new Promise((resolve, reject) => {
            axios.post(`/api/manual-credit/player/${payload.playerId}`, payload)
                .then(resp => {
                    commit('setBusy', false);
                    resolve(resp.data);
                })
                .catch(err => {
                    commit('setBusy', false);
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    getBonuses({state, commit}, payload) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/bonus-definition?limit=10&offset=0`)
                .then(resp => {
                    commit('setBonuses', resp.data.items);
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    setManualBonus({state, commit}, payload) {
        return new Promise((resolve, reject) => {
            axios.post(`/api/wallet/bonus/create`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    loadItems({state, commit, rootState}, data) {
        commit('setBusy', true);
        axios.get(`/api/client-security/player?limit=${data.params.limit}&offset=${data.params.offset}${createFilterQuery(data.filters)}${data.sort}`)
            .then(function (response) {
                commit('setItems', response);
            })
            .catch(error => {
                console.log(error);
            });
    },
    // eslint-disable-next-line no-unused-vars
    getPlayer({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/client-security/player/${id}`)
                .then(resp => {
                    commit('setPlayerData', {...resp.data, wallets: []});
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    getPlayerWallet({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/wallet`, {params: {playerId: id}})
                .then(resp => {
                    commit('setPlayerData', {...state.playerData, wallets: resp.data.items});
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    getPlayerStatistics({state, commit}, payload) {
        const url = payload.dateFrom ?
            `/api/client-security/player/${payload.id}/statistics?fromDate=${payload.dateFrom}&toDate=${payload.dateTo}` :
            `/api/client-security/player/${payload.id}/statistics`;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then(resp => {
                    commit('setPlayerData', {...state.playerData, statistics: resp.data});
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    getPlayerLoginHistory({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/client-security/player/${id}/login-history-entry`)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    getPlayerBankAccounts({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/client-security/player/${id}/bank-accounts`)
                .then(resp => {
                    commit('setPlayerBankAccounts', resp.data);
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    getPlayerNotes({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/crm/note`, {params: {objectId: id, limit: 10, offset: 0}})
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    sendEmailMessage({state, commit}, payload) {
        return new Promise((resolve, reject) => {
            axios.post(`/api/inbox/message-to-player/compose/email`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    sendSmsMessage({state, commit}, payload) {
        return new Promise((resolve, reject) => {
            axios.post(`/api/inbox/message-to-player/compose/sms`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    resendPasswordResetEmail({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/${id}/resend-password-reset-email`)
                .then(resp => {
                    commit('setResendPasswordResetEmailMessage', 'label.email_was_sent');
                    resolve(resp.data);
                })
                .catch(err => {
                    commit('setResendPasswordResetEmailMessage', err.response.data.title);
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    resendActivationEmail({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/${id}/resend-activation-email`)
                .then(resp => {
                    commit('setResendActivationEmailMessage', 'label.email_was_sent');
                    resolve(resp.data);
                })
                .catch(err => {
                    commit('setResendActivationEmailMessage', err.response.data.title);
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    deletePlayer({state, commit}, id) {
        return new Promise((resolve, reject) => {
            axios.delete(`/api/client-security/player/${id}`)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    requestForKyc({state, commit}, form) {
        const payload = {
            emailMessage: form.emailMessage
        }
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/${form.id}/kyc/verification`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    acceptKycDocument({state, commit}, form) {
        const payload = {
            emailMessage: form.emailMessage
        }
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/${form.id}/kyc/document/${form.item}/accepted`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    changePlayerKycDetails({state, commit}, form) {
        return new Promise((resolve, reject) => {
            axios.put(`/api/client-security/player/${form.id}/kyc`, form)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    rejectKycDocument({state, commit}, form) {
        const payload = {
            documentName: form.documentName,
            emailMessage: form.emailMessage
        }
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/${form.id}/kyc/document/${form.item}/canceled`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    approvePlayerKyc({state, commit}, form) {
        const payload = {
            emailMessage: form.emailMessage
        }
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/${form.id}/kyc/approving`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    rejectPlayerKyc({state, commit}, form) {
        const payload = {
            emailMessage: form.emailMessage
        }
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/${form.id}/kyc/rejection`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    getCmsResource(context, payload) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/resource/key/default/${payload.locale}/${payload.resourceKeyName}`)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    lockPlayer({state, commit}, id) {
        const payload = {
            playerId: id
        }

        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/lock`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    unlockPlayer({state, commit}, id) {
        const payload = {
            playerId: id
        }
        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/unlock`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // eslint-disable-next-line no-unused-vars
    unlockAllowToLogin({state, commit}, id) {
        const payload = {
            playerId: id
        }

        return new Promise((resolve, reject) => {
            axios.post(`/api/client-security/player/unblock`, payload)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    changeAgreement(context, payload) {
        return new Promise((resolve, reject) => {
            axios.patch(
                `/api/client-security/player/${payload.playerId}/change-agreements`, payload.agreements)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    resetAvatar(context, id) {
        return new Promise((resolve, reject) => {
            axios.post(
                `/api/client-security/player/${id}/reset-avatar`)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    }
}

function createFilterQuery(query) {
    let filter = '';
    Object.keys(query).forEach(key => {
        filter += query[key];

        if (key === 'phone') {
            filter += '&filters[phone][0][operator]=like'
        }

        if (key === 'phrase') {
            filter += '&filters[phrase][0][operator]=like'
        }

    })
    return filter;
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
